import React from 'react';
import { Box, Grid, Typography } from '@mui/material';
import {
  CheckCircleOutline as FoundIcon,
  HelpOutline as MissedIcon,
} from '@mui/icons-material';
import type { DiscoveryQuality } from '../types';
import SectionShell from './SectionShell';

const FindingList: React.FC<{ title: string; entries: string[]; icon: React.ReactNode; empty: string }> = ({
  title, entries, icon, empty,
}) => (
  <Box>
    <Typography variant="subtitle2" gutterBottom>{title}</Typography>
    {entries.length === 0 && <Typography variant="body2" color="text.secondary">{empty}</Typography>}
    {entries.map((entry, idx) => (
      <Box key={idx} display="flex" gap={1} mb={0.75} alignItems="flex-start">
        {icon}
        <Typography variant="body2">{entry}</Typography>
      </Box>
    ))}
  </Box>
);

/** What the rep found out about the buyer, and what the buyer was holding back. */
const DiscoveryQualitySection: React.FC<{ data?: DiscoveryQuality }> = ({ data }) => {
  if (!data) return null;
  return (
    <SectionShell title="Discovery" score={data.overallScore} summary={data.overallSummary}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        {data.questionsAsked} discovery question{data.questionsAsked === 1 ? '' : 's'} asked
      </Typography>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <FindingList
            title="Uncovered"
            entries={data.uncovered ?? []}
            icon={<FoundIcon color="success" fontSize="small" sx={{ mt: 0.25 }} />}
            empty="Nothing the customer was holding back came out."
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <FindingList
            // Questions the rep could have asked; never the AI customer's
            // private brief (owner, 2026-09-27).
            title="Questions to ask next time"
            entries={data.missed ?? []}
            icon={<MissedIcon color="warning" fontSize="small" sx={{ mt: 0.25 }} />}
            empty="No discovery gaps: the rep asked about everything that mattered."
          />
        </Grid>
      </Grid>
    </SectionShell>
  );
};

export default DiscoveryQualitySection;
